import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SERVER_CONFIG from '../services/config';
import SearchModal from './SearchModal';
import ArrivalModal from './ArrivalModal';
import MedicineRevisionModal from './MedicineRevisionModal';
import '../styles/InventoryPage.css';

const API_URL = SERVER_CONFIG.apiUrl;

function expiryColorStyle(dateStr) {
  if (!dateStr) return {};
  try {
    const today = new Date();
    const exp = new Date(dateStr);
    const diffMs = exp - today;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0)   return { background: '#5a1010', color: '#ff6b6b' };
    if (diffDays < 30)  return { background: '#4a1a1a', color: '#ff8888' };
    if (diffDays < 90)  return { background: '#3a2a00', color: '#ffcc44' };
    return { background: '#0d2a0d', color: '#88cc88' };
  } catch (e) {
    return {};
  }
}

function formatExpiry(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return mm + '/' + yyyy;
  } catch (e) {
    return dateStr;
  }
}

function parsePack1Size(val) {
  if (!val) return null;
  const n = parseFloat(String(val).replace(',', '.'));
  return isNaN(n) ? null : n;
}

function getFormType(form) {
  if (!form) return 'solid';
  const f = form.toUpperCase();
  if (
    f.includes('РАСТВОР') || f.includes('СПРЕЙ') || f.includes('КАПЛИ') ||
    f.includes('СУСПЕНЗИЯ') || f.includes('СИРОП') || f.includes('ГЕЛЬ') ||
    f.includes('КРЕМ') || f.includes('МАЗЬ') || f.includes('ПОРОШОК') ||
    f.includes('ЛИНИМЕНТ')
  ) return 'liquid';
  return 'solid';
}

function totalTablets(row) {
  const fType = getFormType(row.form);
  if (fType === 'liquid') return null;
  const pack1Size = parsePack1Size(row.pack1_size);
  if (!pack1Size) return null;
  return (row.whole_inners || 0) * pack1Size + (row.partial_units || 0);
}

// Группировка позиций лекарств по штрихкоду
function groupItemsByBarcode(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.barcode || ('__no_bc__' + item.sync_id);
    if (!groups.has(key)) {
      groups.set(key, { key, barcode: item.barcode || null, name: item.name, items: [] });
    }
    groups.get(key).items.push(item);
  }
  return Array.from(groups.values());
}

function SupplyPhoto({ photo, size, onClick, placeholder }) {
  const [broken, setBroken] = useState(false);
  const sizeClass = 'supply-photo supply-photo--' + size;
  if (!photo || broken) {
    return <div className={sizeClass + ' supply-photo--none'}>{placeholder || '📦'}</div>;
  }
  return (
    <img
      src={'/supply-photos/' + photo}
      alt=""
      className={sizeClass}
      loading="lazy"
      onClick={onClick}
      onError={() => setBroken(true)}
    />
  );
}

function InventoryPage({ user }) {
  const [activeTab, setActiveTab] = useState('food');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Food/waste qty
  const [pendingQty, setPendingQty] = useState({});
  const [savingIds, setSavingIds] = useState(new Set());
  const [qtyConfirm, setQtyConfirm] = useState(null); // { syncId, qty, itemName, oldQty }

  // Medicine whole_inners
  const [pendingInners, setPendingInners] = useState({});
  const [savingInnersIds, setSavingInnersIds] = useState(new Set());

  // Medicine partial (units or percent)
  const [pendingPartial, setPendingPartial] = useState({});
  const [savingPartialIds, setSavingPartialIds] = useState(new Set());

  // Medicine groups expand/delete/edit
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [medEditItem, setMedEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { syncId, itemName }

  // Modals
  const [searchOpen, setSearchOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [medRevisionOpen, setMedRevisionOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const openPhoto = (photo) => (e) => {
    e.stopPropagation();
    setPhotoPreview('/supply-photos/' + photo);
  };

  // Green highlight (long press)
  const [checkedIds, setCheckedIds] = useState(new Set());
  const longPressTimer = React.useRef(null);

  // Revision mode (persisted in localStorage)
  const [revisionMode, setRevisionMode] = useState(() => localStorage.getItem('inv_revision') === '1');
  const [revisionConfirm, setRevisionConfirm] = useState(null); // null | 'start' | 'stop'
  const [revisionIds, setRevisionIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('inv_revision_ids') || '[]')); }
    catch { return new Set(); }
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: 'Bearer ' + token };

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API_URL + '/inventory', {
        params: { category: activeTab },
        headers,
      });
      setSections(data);
      setPendingQty({});
      setPendingInners({});
      setPendingPartial({});
    } catch (err) {
      console.error('[Inventory] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadSections(); }, [loadSections]);

  // ── Food/waste ──────────────────────────────────────────────────────────────

  const handleQtyChange = (syncId, value) => {
    const v = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setPendingQty(prev => ({ ...prev, [syncId]: v }));
  };

  const doQtySave = async (syncId, qty) => {
    setSavingIds(prev => new Set([...prev, syncId]));
    try {
      await axios.patch(
        API_URL + '/inventory/items/' + syncId + '/quantity',
        { quantity: qty },
        { headers }
      );
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.map(item =>
          item.sync_id === syncId ? { ...item, quantity: qty } : item
        ),
      })));
      setSections(prev => prev.map(section => {
        let current_amount = 0;
        for (const item of section.items) {
          if (section.unit === 'шт' || !item.volume_value) {
            current_amount += item.quantity;
          } else {
            current_amount += (item.volume_value || 0) * item.quantity;
          }
        }
        return { ...section, current_amount };
      }));
      setPendingQty(prev => { const n = { ...prev }; delete n[syncId]; return n; });
      setCheckedIds(prev => new Set([...prev, syncId]));
    } catch (err) {
      console.error('[Inventory] qty save error:', err);
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(syncId); return n; });
    }
  };

  const handleQtySave = (syncId) => {
    const raw = pendingQty[syncId];
    if (raw === undefined || raw === '') return;
    const qty = parseInt(raw);
    if (isNaN(qty)) return;
    const item = sections.flatMap(s => s.items || []).find(i => i.sync_id === syncId);
    const itemName = item?.name || '';
    const oldQty = item?.quantity ?? 0;
    if (qty === oldQty) {
      setPendingQty(prev => { const n = { ...prev }; delete n[syncId]; return n; });
      return;
    }
    setQtyConfirm({ syncId, qty, itemName, oldQty });
  };

  // ── Medicine whole_inners ───────────────────────────────────────────────────

  const handleInnersChange = (syncId, value) => {
    const v = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setPendingInners(prev => ({ ...prev, [syncId]: v }));
  };

  const handleInnersSave = async (syncId) => {
    const raw = pendingInners[syncId];
    if (raw === undefined || raw === '') return;
    const val = parseInt(raw);
    if (isNaN(val)) return;
    setSavingInnersIds(prev => new Set([...prev, syncId]));
    try {
      await axios.patch(
        API_URL + '/inventory/items/' + syncId + '/medicine-status',
        { whole_inners: val },
        { headers }
      );
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.map(item =>
          item.sync_id === syncId ? { ...item, whole_inners: val } : item
        ),
      })));
      setPendingInners(prev => { const n = { ...prev }; delete n[syncId]; return n; });
      setCheckedIds(prev => new Set([...prev, syncId]));
    } catch (err) {
      console.error('[Inventory] inners save error:', err);
    } finally {
      setSavingInnersIds(prev => { const n = new Set(prev); n.delete(syncId); return n; });
    }
  };

  // ── Medicine partial ────────────────────────────────────────────────────────

  const handlePartialChange = (syncId, value) => {
    const v = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setPendingPartial(prev => ({ ...prev, [syncId]: v }));
  };

  const handlePartialSave = async (syncId, fType) => {
    const raw = pendingPartial[syncId];
    if (raw === undefined || raw === '') return;
    const val = parseInt(raw);
    if (isNaN(val)) return;
    setSavingPartialIds(prev => new Set([...prev, syncId]));
    try {
      const body = fType === 'liquid'
        ? { partial_percent: val }
        : { partial_units: val };
      await axios.patch(
        API_URL + '/inventory/items/' + syncId + '/medicine-status',
        body,
        { headers }
      );
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.map(item =>
          item.sync_id === syncId
            ? { ...item, ...(fType === 'liquid' ? { partial_percent: val } : { partial_units: val }) }
            : item
        ),
      })));
      setPendingPartial(prev => { const n = { ...prev }; delete n[syncId]; return n; });
      setCheckedIds(prev => new Set([...prev, syncId]));
    } catch (err) {
      console.error('[Inventory] partial save error:', err);
    } finally {
      setSavingPartialIds(prev => { const n = new Set(prev); n.delete(syncId); return n; });
    }
  };

  // ── Long press & revision ───────────────────────────────────────────────────

  const handleRowPointerDown = (syncId) => {
    longPressTimer.current = setTimeout(() => {
      if (revisionMode) {
        setRevisionIds(prev => {
          const next = new Set(prev);
          if (next.has(syncId)) next.delete(syncId);
          else next.add(syncId);
          localStorage.setItem('inv_revision_ids', JSON.stringify([...next]));
          return next;
        });
      } else {
        setCheckedIds(prev => {
          const next = new Set(prev);
          if (next.has(syncId)) next.delete(syncId);
          else next.add(syncId);
          return next;
        });
      }
    }, 600);
  };

  const handleRowPointerUp = () => {
    clearTimeout(longPressTimer.current);
  };

  const markRevision = useCallback((syncId) => {
    if (!syncId) return;
    setRevisionIds(prev => {
      const next = new Set(prev);
      next.add(syncId);
      localStorage.setItem('inv_revision_ids', JSON.stringify([...next]));
      return next;
    });
  }, []);

  const toggleRevision = () => {
    if (revisionMode) {
      setRevisionConfirm('stop');
    } else {
      setRevisionConfirm('start');
    }
  };

  const confirmRevision = async () => {
    if (revisionConfirm === 'start') {
      setRevisionMode(true);
      localStorage.setItem('inv_revision', '1');
      // Записываем время начала ревизии
      try {
        await axios.post(API_URL + '/inventory/revision', { category: activeTab }, { headers });
      } catch (e) {
        console.warn('[revision] не удалось сохранить время ревизии:', e.message);
      }
    } else {
      setRevisionMode(false);
      setRevisionIds(new Set());
      localStorage.removeItem('inv_revision');
      localStorage.removeItem('inv_revision_ids');
    }
    setRevisionConfirm(null);
  };

  // ── Medicine groups ──────────────────────────────────────────────────────────

  const toggleExpand = (key) => {
    setExpandedGroups(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };


  const deleteItem = async (syncId) => {
    setDeletingIds(prev => new Set([...prev, syncId]));
    try {
      await axios.delete(API_URL + '/inventory/items/' + syncId, { headers });
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.filter(item => item.sync_id !== syncId),
      })));
    } catch (err) {
      console.error('[Inventory] delete error:', err);
    } finally {
      setDeletingIds(prev => { const n = new Set(prev); n.delete(syncId); return n; });
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const sectionProgress = (section) => {
    if (!section.target_amount || section.target_amount <= 0) return null;
    return Math.min(100, (section.current_amount / section.target_amount) * 100);
  };

  const progressClass = (pct) => {
    if (pct === null) return 'neutral';
    if (pct < 25) return 'red';
    if (pct < 50) return 'yellow';
    return 'green';
  };

  const fmt = (val) => {
    if (val == null) return '0';
    const n = Number(val);
    return n % 1 === 0 ? n.toLocaleString('ru-RU') : n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  };

  const isMedicineTab = activeTab === 'medicine';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="inventory-page">
      <div className="inv-tabs">
        <button
          className={'inv-tab' + (activeTab === 'food' ? ' active' : '')}
          onClick={() => setActiveTab('food')}
        >Корма</button>
        <button
          className={'inv-tab' + (activeTab === 'medicine' ? ' active' : '')}
          onClick={() => setActiveTab('medicine')}
        >Лекарства</button>
        <button
          className={'inv-tab' + (activeTab === 'waste' ? ' active' : '')}
          onClick={() => setActiveTab('waste')}
        >💩</button>
        <div className="inv-search-group">
          <button
            className={'inv-revision-btn' + (revisionMode ? ' inv-revision-btn--active' : '') + (revisionConfirm ? ' inv-revision-btn--pending' : '')}
            onClick={toggleRevision}
            title={revisionMode ? 'Ревизия активна — нажмите чтобы выключить' : 'Режим ревизии'}
          >👁</button>
          <button className="inv-search-btn" onClick={() => setSearchOpen(true)} title="Найти по штрихкоду">
            Поиск
          </button>
        </div>
      </div>
      <div className="inv-action-row">
        <button className="inv-add-btn" onClick={() => setAddOpen(true)} title="Добавить позицию">
          + Добавить позицию
        </button>
      </div>

      {loading && <div className="inv-loading">Загрузка…</div>}

      {!loading && sections.length === 0 && (
        <div className="inv-empty">Нет данных</div>
      )}

      {!loading && sections.map(section => {
        const pct = sectionProgress(section);
        const colorCls = progressClass(pct);
        const itemCount = (section.items || []).length;

        return (
          <div key={section.sync_id} className="inv-section">
            <div className={'inv-section-header color-' + colorCls}>
              {pct !== null && (
                <div className="inv-progress-wrap">
                  <div className="inv-progress-fill" style={{ width: pct + '%' }} />
                </div>
              )}
              <div className="inv-section-title-row">
                <span className="inv-section-name">{section.name}</span>
                {section.target_amount ? (
                  <span className="inv-section-amount">
                    {fmt(section.current_amount)} / {fmt(section.target_amount)} {section.unit}
                  </span>
                ) : section.unit ? (
                  <span className="inv-section-amount">
                    {fmt(section.current_amount)} {section.unit}
                  </span>
                ) : (
                  <span className="inv-section-amount">{itemCount} поз.</span>
                )}
              </div>
            </div>

            {section.items.length === 0 ? (
              <div className="inv-no-items">Позиции не добавлены</div>
            ) : isMedicineTab ? (
              /* Сгруппированный вид лекарств */
              <div className="med-groups">
                {groupItemsByBarcode(section.items).map(group => {
                  const confirmed = group.items.filter(i => revisionIds.has(i.sync_id) || checkedIds.has(i.sync_id)).length;
                  const total = group.items.length;
                  const pct = total > 0 ? Math.round(confirmed / total * 100) : 0;
                  const isExpanded = expandedGroups.has(group.key);
                  const totalQty = group.items.reduce((s, i) => s + (i.quantity || 0), 0);
                  const hasCis = group.items.some(i => i.cis);
                  const allDone = confirmed === total && total > 0;
                  const groupPhoto = group.items.find(i => i.photo)?.photo || null;

                  return (
                    <div key={group.key} className="med-group">
                      <div
                        className={'med-group-row' + (allDone && revisionMode ? ' row-checked' : '')}
                        onClick={() => toggleExpand(group.key)}
                        onPointerDown={() => handleRowPointerDown(group.items[0].sync_id)}
                        onPointerUp={handleRowPointerUp}
                        onPointerLeave={handleRowPointerUp}
                        style={{ userSelect: 'none' }}
                      >
                        <SupplyPhoto
                          photo={groupPhoto}
                          size="md"
                          onClick={groupPhoto ? openPhoto(groupPhoto) : undefined}
                        />
                        <span className={'med-group-badge ' + (hasCis ? 'badge-chz' : 'badge-bk')}>
                          {hasCis ? 'ЧЗ' : 'ШК'}
                        </span>
                        <span className="med-group-name">{group.name}</span>
                        {revisionMode ? (
                          <div className="med-group-bar-wrap">
                            <div className="med-group-bar-fill" style={{ width: pct + '%' }} />
                            <span className="med-group-bar-text">{confirmed}/{total}</span>
                          </div>
                        ) : (
                          <span className="med-group-qty">{totalQty} шт</span>
                        )}
                        <span className="med-group-chevron">{isExpanded ? '▾' : '▸'}</span>
                      </div>

                      {isExpanded && (
                        <div className="med-group-items">
                          {group.items.map(item => {
                            const isMarked = revisionIds.has(item.sync_id) || checkedIds.has(item.sync_id);
                            const isDeleting = deletingIds.has(item.sync_id);
                            const isBarcodeItem = !item.cis;
                            const currentVal = pendingQty[item.sync_id] !== undefined ? pendingQty[item.sync_id] : item.quantity;
                            const isSaving = savingIds.has(item.sync_id);
                            return (
                              <div
                                key={item.sync_id}
                                className={'med-group-item' + (isMarked ? ' item-marked' : '')}
                                onClick={isBarcodeItem ? undefined : () => { setMedEditItem(item); setSearchOpen(true); }}
                                style={{ cursor: isBarcodeItem ? 'default' : 'pointer' }}
                              >
                                <span className="med-group-item-dot">{isMarked ? '●' : '○'}</span>
                                {item.photo ? (
                                  <SupplyPhoto
                                    photo={item.photo}
                                    size="sm"
                                    onClick={openPhoto(item.photo)}
                                  />
                                ) : null}
                                <span className="med-group-item-label">
                                  {item.name}
                                  {item.volume_value != null ? ' · ' + item.volume_value + ' ' + (item.unit || '') : ''}
                                  {item.expiry_date ? ' · до ' + item.expiry_date.slice(0, 7) : ''}
                                  {(() => {
                                    if (item.volume_value == null) return null;
                                    const pc1 = item.pack1_count || 1;
                                    const pc2 = item.pack2_count || 1;
                                    const total = pc1 * pc2;
                                    if (total <= 1) return null;
                                    const f = (item.form || '').toLowerCase();
                                    let formUnit = 'шт.';
                                    if (f.includes('таблет')) formUnit = 'таблеток';
                                    else if (f.includes('капсул')) formUnit = 'капсул';
                                    else if (f.includes('гранул')) formUnit = 'гранул';
                                    else if (f.includes('ампул')) formUnit = 'ампул';
                                    else if (f.includes('порошок')) formUnit = 'доз';
                                    const label = item.volume_value + ' ' + (item.unit || '');
                                    const formula = label + ' × ' + pc1 + ' × ' + pc2 + ' = ' + total + ' ' + formUnit;
                                    return <span style={{ display: 'block', fontSize: '0.82em', color: '#999', marginTop: 1 }}>{formula}</span>;
                                  })()}
                                </span>
                                {isBarcodeItem && !isMarked && (
                                  <div className="qty-wrap" onClick={e => e.stopPropagation()}>
                                    <input
                                      type="number"
                                      min="0"
                                      className={'qty-input' + (isSaving ? ' saving' : '')}
                                      value={currentVal}
                                      onChange={e => handleQtyChange(item.sync_id, e.target.value)}
                                      onBlur={() => handleQtySave(item.sync_id)}
                                      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                                      disabled={isSaving}
                                    />
                                    {isSaving && <span className="qty-spinner">...</span>}
                                  </div>
                                )}
                                {!isBarcodeItem && !isMarked && (
                                  <button
                                    className="med-group-item-del"
                                    disabled={isDeleting}
                                    onClick={e => { e.stopPropagation(); setDeleteConfirm({ syncId: item.sync_id, itemName: item.name }); }}
                                  >
                                    {isDeleting ? '…' : '🗑'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Таблица для кормов/расходников */
              <table className="inv-table">
                <thead>
                  <tr>
                    <th className="col-photo"></th>
                    <th className="col-name">Наименование</th>
                    {section.unit && section.unit !== 'шт' && (
                      <th className="col-vol">Объём, {section.unit}</th>
                    )}
                    <th className="col-qty">Кол-во, шт</th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map(item => {
                    const sid = item.sync_id;
                    const currentVal = pendingQty[sid] !== undefined ? pendingQty[sid] : item.quantity;
                    const isSaving = savingIds.has(sid);
                    const isDirty = pendingQty[sid] !== undefined;
                    const isChecked = checkedIds.has(sid) || revisionIds.has(sid);

                    return (
                      <tr
                        key={sid}
                        className={isChecked ? 'row-checked' : isDirty ? 'row-dirty' : ''}
                        onPointerDown={() => handleRowPointerDown(sid)}
                        onPointerUp={handleRowPointerUp}
                        onPointerLeave={handleRowPointerUp}
                        style={{ userSelect: 'none' }}
                      >
                        <td className="col-photo">
                          <SupplyPhoto
                            photo={item.photo}
                            size="lg"
                            onClick={item.photo ? openPhoto(item.photo) : undefined}
                          />
                        </td>
                        <td className="col-name">{item.name}</td>
                        {section.unit && section.unit !== 'шт' && (
                          <td className="col-vol">
                            {item.volume_value != null ? fmt(item.volume_value) : '—'}
                          </td>
                        )}
                        <td className="col-qty">
                          <div className="qty-wrap">
                            <input
                              type="number"
                              min="0"
                              className={'qty-input' + (isSaving ? ' saving' : '')}
                              value={currentVal}
                              onChange={e => handleQtyChange(sid, e.target.value)}
                              onBlur={() => handleQtySave(sid)}
                              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                              disabled={isSaving}
                            />
                            {isSaving && <span className="qty-spinner">...</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {qtyConfirm && (
        <div className="rev-confirm-overlay" onClick={() => {
          setPendingQty(prev => { const n = { ...prev }; delete n[qtyConfirm.syncId]; return n; });
          setQtyConfirm(null);
        }}>
          <div className="rev-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="rev-confirm-title" style={{ fontSize: 15, textAlign: 'left' }}>{qtyConfirm.itemName}</div>
            <div className="rev-confirm-text" style={{ textAlign: 'left', fontSize: 14, marginTop: 2 }}>
              Было: <b>{qtyConfirm.oldQty} шт</b> → Станет: <b style={{ color: qtyConfirm.qty === 0 ? '#e05a5a' : '#7dda8a' }}>{qtyConfirm.qty} шт</b>
            </div>
            {qtyConfirm.qty === 0 && (
              <div className="rev-confirm-text" style={{ color: '#e05a5a', marginTop: 2 }}>
                Позиция исчезнет из списка.
              </div>
            )}
            <div className="rev-confirm-actions">
              <button className="rev-confirm-cancel" onClick={() => {
                setPendingQty(prev => { const n = { ...prev }; delete n[qtyConfirm.syncId]; return n; });
                setQtyConfirm(null);
              }}>Отмена</button>
              <button
                className={'rev-confirm-ok' + (qtyConfirm.qty === 0 ? ' rev-confirm-ok--stop' : '')}
                onClick={() => { doQtySave(qtyConfirm.syncId, qtyConfirm.qty); setQtyConfirm(null); }}
              >Сохранить {qtyConfirm.qty} шт</button>
            </div>
          </div>
        </div>
      )}

      {revisionConfirm && (
        <div className="rev-confirm-overlay" onClick={() => setRevisionConfirm(null)}>
          <div className="rev-confirm-box" onClick={e => e.stopPropagation()}>
            {revisionConfirm === 'start' ? (
              <>
                <div className="rev-confirm-title">Начать ревизию?</div>
                <div className="rev-confirm-text">Позиции будут помечаться зелёным по мере проверки.</div>
              </>
            ) : (
              <>
                <div className="rev-confirm-title rev-confirm-title--stop">Завершить ревизию?</div>
                <div className="rev-confirm-text">Все отметки будут сброшены.</div>
              </>
            )}
            <div className="rev-confirm-actions">
              <button className="rev-confirm-cancel" onClick={() => setRevisionConfirm(null)}>Отмена</button>
              <button
                className={'rev-confirm-ok' + (revisionConfirm === 'stop' ? ' rev-confirm-ok--stop' : '')}
                onClick={confirmRevision}
              >
                {revisionConfirm === 'start' ? 'Начать' : 'Завершить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="rev-confirm-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="rev-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="rev-confirm-title rev-confirm-title--stop">Удалить позицию?</div>
            <div className="rev-confirm-text" style={{ textAlign: 'left', fontSize: 14, marginTop: 2 }}>
              <b>{deleteConfirm.itemName}</b> будет удалена из базы.
            </div>
            <div className="rev-confirm-actions">
              <button className="rev-confirm-cancel" onClick={() => setDeleteConfirm(null)}>Отмена</button>
              <button
                className="rev-confirm-ok rev-confirm-ok--stop"
                onClick={() => { deleteItem(deleteConfirm.syncId); setDeleteConfirm(null); }}
              >Удалить</button>
            </div>
          </div>
        </div>
      )}

      <SearchModal
        isOpen={searchOpen}
        onClose={() => { setSearchOpen(false); setMedEditItem(null); }}
        activeTab={activeTab}
        sections={sections}
        onDone={loadSections}
        revisionMode={revisionMode}
        revisionIds={revisionIds}
        onRevisionMark={markRevision}
        onAutoTab={cat => setActiveTab(cat)}
        initialItem={medEditItem}
      />
      <ArrivalModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={loadSections}
        onAutoTab={cat => setActiveTab(cat)}
      />
      <MedicineRevisionModal
        isOpen={medRevisionOpen}
        onClose={() => setMedRevisionOpen(false)}
        onDone={loadSections}
      />

      {photoPreview && (
        <div className="inv-photo-modal-overlay" onClick={() => setPhotoPreview(null)}>
          <img src={photoPreview} alt="" className="inv-photo-modal-img" />
        </div>
      )}
    </div>
  );
}

export default InventoryPage;
